import { useEffect, useMemo, useRef, useState } from "react";
import { StepContainer } from "../components/StepContainer";
import { IdentityFields, type IdentityFieldsValues } from "../components/fields/IdentityFields";
import {
  SocialFields,
  type SocialChannels,
} from "../components/fields/SocialFields";
import { Label } from "@/components/ui/label";
import { useUpdateBusinessContext } from "../hooks/useUpdateBusinessContext";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { STEPS, findStepIndexBySlug } from "../config/steps";

export interface IdentityStepProps {
  onValidityChange: (valid: boolean) => void;
  onAttemptContinue: (continueFn: () => Promise<boolean>) => void;
  onMissingChange?: (labels: string[]) => void;
}

export function IdentityStep({
  onValidityChange,
  onAttemptContinue,
  onMissingChange,
}: IdentityStepProps) {
  const step = STEPS[findStepIndexBySlug("identidade")];
  const { mutateDebounced, mutate } = useUpdateBusinessContext();
  const { query } = useBusinessContext();
  const formRef = useRef<{
    trigger: () => Promise<boolean>;
    getValues: () => IdentityFieldsValues;
  } | null>(null);

  const defaults = useMemo<Partial<IdentityFieldsValues> | null>(() => {
    const row = query.data;
    if (!row) return null;
    return {
      honorific_prefix:
        (row.honorific_prefix as IdentityFieldsValues["honorific_prefix"] | null | undefined) ?? "",
      display_name: row.display_name ?? "",
      gender: (row.gender ?? undefined) as IdentityFieldsValues["gender"] | undefined,
      age: row.age ?? undefined,
      photo_url: row.photo_url ?? null,
    };
  }, [query.data]);

  const [social, setSocial] = useState<SocialChannels>({});
  const socialSeededRef = useRef(false);
  useEffect(() => {
    if (socialSeededRef.current) return;
    const row = query.data;
    if (!row) return;
    socialSeededRef.current = true;
    const ch = (row.social_channels ?? {}) as SocialChannels;
    setSocial({
      instagram: ch.instagram ?? undefined,
      tiktok: ch.tiktok ?? undefined,
      youtube: ch.youtube ?? undefined,
      website: ch.website ?? undefined,
    });
  }, [query.data]);

  const socialRef = useRef(social);
  socialRef.current = social;

  const handleSocialChange = (next: SocialChannels) => {
    setSocial(next);
    mutateDebounced({ social_channels: next } as never);
  };

  useEffect(() => {
    onAttemptContinue(async () => {
      const ok = await formRef.current?.trigger();
      if (!ok) return false;
      const values = formRef.current!.getValues();
      await mutate({
        honorific_prefix: values.honorific_prefix ?? "",
        display_name: values.display_name,
        gender: values.gender,
        age: values.age,
        photo_url: values.photo_url ?? null,
        social_channels: socialRef.current,
      } as never);
      return true;
    });
  }, [mutate, onAttemptContinue]);

  return (
    <StepContainer icon={step.icon} headline={step.headline} sub={step.sub}>
      {!query.isLoading ? (
        <div className="space-y-6">
          <IdentityFields
            key={defaults ? "loaded" : "empty"}
            defaultValues={defaults ?? undefined}
            validateOnMount
            onChange={(patch) => mutateDebounced(patch)}
            onValidityChange={onValidityChange}
            onMissingChange={onMissingChange}
            registerForm={(f) => {
              formRef.current = { trigger: f.trigger, getValues: f.getValues };
            }}
          />
          <div className="space-y-3 border-t border-border pt-5">
            <div>
              <Label className="text-base font-medium">Presença online (opcional)</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Onde a IA pode encontrar referências do seu jeito de comunicar.
              </p>
            </div>
            <SocialFields value={social} onChange={handleSocialChange} />
          </div>
        </div>
      ) : null}
    </StepContainer>
  );
}
